import { useFormState, useWatch } from 'react-hook-form';
import { OfficeInfoCollection } from '~/constants/ff.posthog';
import { isUnanswered } from '~/domain/OfficeMissingInfo';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { requiredOk } from '../../../utils';

export const useOfficeInformation = (control) => {
  // GOTU-9592: # operatories becomes required alongside the shift-request missing-info modal.
  const isOperatoriesRequired = useFeatureFlag(OfficeInfoCollection);
  // Under the same flag, time per patient no longer offers 'N/A' here: the modal treats a stored 'N/A' as
  // missing and asks again at shift request time, so offering it in Account Information sent an office back
  // and forth between the two screens (GOTU-14830). A stored 'N/A' still reads as unanswered below.
  const hidesNotApplicable = isOperatoriesRequired;

  const watchedValues = useWatch({
    control,
    name: [
      'timePerPatientRDH',
      'timePerPatientDA',
      'timePerPatientDN',
      'isAssistedHygiene',
      'productivity',
      'partnershipOrganization',
      'partnershipCode',
    ],
  });

  const { dirtyFields } = useFormState({ control });

  const formValues = {
    timePerPatientRDH: watchedValues[0],
    timePerPatientDA: watchedValues[1],
    timePerPatientDN: watchedValues[2],
    isAssistedHygiene: watchedValues[3],
    productivity: watchedValues[4],
    partnershipOrganization: watchedValues[5],
    partnershipCode: watchedValues[6],
  };

  const timeFields = ['timePerPatientRDH', 'timePerPatientDA', 'timePerPatientDN'];

  const isTimerPerPatientValid =
    timeFields.every((key) => formValues[key] !== '') && // Ensure no field is empty
    timeFields.some((key) => formValues[key] !== 'N/A'); // At least one field is not 'N/A'

  const isAssistedHygieneValid = formValues.isAssistedHygiene !== undefined;

  // 'N/A' doesn't count as an answer, the same rule the missing-info modal applies.
  const isOperatoriesValid =
    !isOperatoriesRequired || !isUnanswered(formValues.productivity?.operatories);

  const isValid = isTimerPerPatientValid && isAssistedHygieneValid && isOperatoriesValid;

  const isAnyTimePerPatientDirty = Boolean(
    dirtyFields.timePerPatientRDH ||
      dirtyFields.timePerPatientDA ||
      dirtyFields.timePerPatientDN,
  );

  const canSave =
    requiredOk(isAnyTimePerPatientDirty, isTimerPerPatientValid) &&
    requiredOk(dirtyFields.isAssistedHygiene, isAssistedHygieneValid) &&
    requiredOk(dirtyFields.productivity?.operatories, isOperatoriesValid);

  return {
    formValues,
    isValid,
    isTimerPerPatientValid,
    isOperatoriesRequired,
    hidesNotApplicable,
    canSave,
  };
};
